import { animated, useTransition } from '@react-spring/web';
import throttle from 'lodash/throttle';
import uniqueId from 'lodash/uniqueId';
import React, {
  Reducer,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  ToastNotificationsControlProvider,
  ToastNotificationsProvider,
  ToastNotificationType,
} from '@tanium/react-toast-notifications-context';
import {
  NOTIFICATION_REMOVAL_THROTTLE_INTERVAL,
  ToastNotification,
} from '@tanium/react-toast-notifications-display';
import { useFocusInOut } from '@tanium/react-use-focus-in-out';
import { replaceOrAppend } from './replaceOrAppend';
import { useRefCount } from './useRefCount';

export interface ToastNotificationWithKey extends ToastNotificationType {
  key: string;
}

interface ManagerState {
  notifications: readonly ToastNotificationWithKey[];
  removalQueue: readonly ToastNotificationWithKey[];
}

type ActionDefinition<T, P = undefined> = P extends undefined
  ? {
      type: T;
    }
  : {
      type: T;
      payload: P;
    };

type Action =
  | ActionDefinition<'ADD_NOTIFICATION', ToastNotificationWithKey>
  | ActionDefinition<'REMOVE_NOTIFICATION', ToastNotificationWithKey>
  | ActionDefinition<'QUEUE_REMOVE_NOTIFICATION', ToastNotificationWithKey>
  | ActionDefinition<'CLEAR_REMOVAL_QUEUE'>
  | ActionDefinition<'PROCESS_REMOVAL_QUEUE'>;

export const initialState = {
  notifications: [],
  removalQueue: [],
};

export const reducer: Reducer<ManagerState, Action> = (state, action) => {
  switch (action.type) {
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [...state.notifications, action.payload],
      };
    case 'REMOVE_NOTIFICATION': {
      const { notificationId, key } = action.payload;

      return {
        ...state,
        notifications: state.notifications.filter((x) =>
          notificationId ? x.notificationId !== notificationId : x.key !== key,
        ),
      };
    }
    case 'QUEUE_REMOVE_NOTIFICATION': {
      if (
        action.payload.notificationId &&
        state.notifications.some(
          ({ notificationId }) => notificationId === action.payload.notificationId,
        )
      ) {
        return {
          ...state,
          removalQueue: replaceOrAppend(
            state.removalQueue,
            (x) => x.notificationId === action.payload.notificationId,
            action.payload,
          ),
        };
      }

      if (
        !state.removalQueue.some(({ key }) => key === action.payload.key) &&
        state.notifications.some(({ key }) => key === action.payload.key)
      ) {
        return { ...state, removalQueue: state.removalQueue.concat(action.payload) };
      }

      return state;
    }
    case 'CLEAR_REMOVAL_QUEUE':
      return { ...state, removalQueue: [] };
    case 'PROCESS_REMOVAL_QUEUE':
      if (state.removalQueue.length > 0) {
        const notificationIdToRemove = state.removalQueue[0].notificationId;
        const keyToRemove = state.removalQueue[0].key;
        return {
          ...state,
          notifications: state.notifications.filter((x) =>
            notificationIdToRemove
              ? x.notificationId !== notificationIdToRemove
              : x.key !== keyToRemove,
          ),
          removalQueue: state.removalQueue.slice(1),
        };
      }
      return state;
    default:
      return state;
  }
};

export const ToastNotificationsWrapper = ({ children }: { children?: React.ReactNode }) => {
  const [{ notifications, removalQueue }, dispatch] = useReducer(reducer, initialState);

  // notifications grouped by their notificationId
  const groupedNotifications = useMemo(
    () =>
      notifications.reduce((notificationsAfterGrouping, notification) => {
        if (notification.notificationId) {
          const indexOfNotificationInGroup = notificationsAfterGrouping.findIndex(
            (i) => i.notificationId === notification.notificationId,
          );

          if (indexOfNotificationInGroup >= 0) {
            notificationsAfterGrouping.splice(indexOfNotificationInGroup, 1);
          }
        }

        return [...notificationsAfterGrouping, notification];
      }, [] as ToastNotificationWithKey[]),
    [notifications],
  );

  const refMap = useRef(new WeakMap());
  const notificationCountMap = useRef<Map<string | undefined, number>>(new Map());

  const [isTimerRunning, setIsTimerRunning] = useState(true);

  const transition = useTransition(groupedNotifications, {
    keys: (n) => n.key,
    from: { opacity: 0, paddingBottom: 8, transform: 'translate3d(0,0,0)' },
    enter: { opacity: 1 },
    update: (item) => ({ height: refMap.current.get(item).offsetHeight }),
    leave: (item) => ({
      opacity: 0,
      paddingBottom: 0,
      height: 0,
      transform: `translate3d(0,-${refMap.current.get(item).offsetHeight}px,0)`,
    }),
  });

  const showNotification = useCallback((notification: ToastNotificationType) => {
    dispatch({
      type: 'ADD_NOTIFICATION',
      payload: { ...notification, key: uniqueId('toast') },
    });
  }, []);

  const queueRemoveNotification = (notification: ToastNotificationWithKey) => {
    dispatch({ type: 'QUEUE_REMOVE_NOTIFICATION', payload: notification });
  };

  const throttledProcessRemovalQueue = useRef(
    throttle(
      () => {
        dispatch({ type: 'PROCESS_REMOVAL_QUEUE' });
      },
      NOTIFICATION_REMOVAL_THROTTLE_INTERVAL,
      // Disable leading-edge call. lodash throttle seems to have a bug where the throttled function
      // is called more often than expected if both leading- and trailing-edge calls are enabled. We
      // can mitigate this issue by disabling leading-edge calls and reducing the timer for
      // notifications by the throttle interval, which should result in the same effective lifetime.
      // see: https://github.com/lodash/lodash/issues/3051
      { leading: false },
    ),
  );

  useEffect(() => {
    if (removalQueue.length) {
      throttledProcessRemovalQueue.current();
    }
  }, [removalQueue]);

  const notificationComponents = transition((style, notification) => {
    if (typeof notification.contents === 'function') {
      const notificationCount = notifications.filter((i) =>
        notification.notificationId
          ? i.notificationId === notification.notificationId
          : i.key === notification.key,
      ).length;

      if (notification.notificationId && notificationCount) {
        notificationCountMap.current.set(notification.notificationId, notificationCount);
      }
    }

    return (
      <animated.div
        // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- function depends on parameter passed to inner function
        ref={(ref) => {
          if (ref) {
            refMap.current.set(notification, ref);
          }
        }}
        style={style}
      >
        <ToastNotification
          type={notification.type}
          isTimerRunning={isTimerRunning}
          // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- function depends on parameter passed to inner function
          onTimerExpired={() => {
            queueRemoveNotification(notification);
          }}
          // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- function depends on parameter passed to inner function
          onDismiss={() => {
            dispatch({ type: 'REMOVE_NOTIFICATION', payload: notification });
          }}
          data-test-id={notification.testId}
          // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop -- object references parameter passed to inner function
          nestedProps={{
            notificationDismissButton: {
              'data-test-id': notification.dismissButtonTestId,
            },
          }}
        >
          {typeof notification.contents === 'function'
            ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              notification.contents(notificationCountMap.current.get(notification.notificationId)!)
            : notification.contents}
        </ToastNotification>
      </animated.div>
    );
  });

  const pauseTimers = useCallback(() => {
    setIsTimerRunning(false);
    throttledProcessRemovalQueue.current.cancel();
    dispatch({ type: 'CLEAR_REMOVAL_QUEUE' });
  }, []);

  const resumeTimers = useCallback(() => {
    setIsTimerRunning(true);
  }, []);

  const { increment: requestPause, decrement: requestResume } = useRefCount({
    onCountZero: resumeTimers,
    onCountExceedZero: pauseTimers,
  });

  const wrapperRef = useFocusInOut({
    handleFocusIn: requestPause,
    handleFocusOut: requestResume,
  });

  const wrappedNotifications = useMemo(
    () => (
      <div ref={wrapperRef} onMouseEnter={requestPause} onMouseLeave={requestResume}>
        {notificationComponents}
      </div>
    ),
    [notificationComponents, requestPause, requestResume, wrapperRef],
  );

  return (
    <ToastNotificationsProvider notifications={wrappedNotifications}>
      <ToastNotificationsControlProvider showNotification={showNotification}>
        {children}
      </ToastNotificationsControlProvider>
    </ToastNotificationsProvider>
  );
};
